import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { useUnits } from '../contexts/UnitsContext';
import { useModal } from '../services/ModalService';
import { lbsToKg } from '../utils/units';
import ModalLoading from './ModalLoading';
import './StatsModal.css';

const RANGES = {
  MONTH: 30 * 24 * 60 * 60 * 1000,
  THREE_MONTHS: 90 * 24 * 60 * 60 * 1000,
  YEAR: 365 * 24 * 60 * 60 * 1000,
  FIVE_YEARS: 5 * 365 * 24 * 60 * 60 * 1000,
  ALL: 'all'
};

const dayKeyMs = (dateLike) => {
  const d = dateLike instanceof Date ? dateLike : new Date(dateLike);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};

const formatDate = (timestamp) => {
  if (!timestamp) return '';
  return new Date(timestamp).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const isFiniteNumber = (value) => typeof value === 'number' && Number.isFinite(value);

const cssEscape = (value) => String(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const PULSE_FRAME_MS = 50;
// 0.25 rad every 50ms in the original interval.
const PULSE_RAD_PER_SECOND = 5;

const StatsModal = ({ close, backendService }) => {
  const { weightUnit } = useUnits();
  const { updateProps } = useModal();
  const chartRef = useRef(null);
  const zoomChartRef = useRef(null);
  const [chartAreaEl, setChartAreaEl] = useState(null);
  const [chartAreaHeight, setChartAreaHeight] = useState(0);

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [zoomRange, setZoomRange] = useState({ start: null, end: null });

  useEffect(() => {
    updateProps({ isUsingUnits: !loading });
  }, [loading, updateProps]);
  const [animateChartIn, setAnimateChartIn] = useState(false);
  const [selectedRange, setSelectedRange] = useState(RANGES.THREE_MONTHS);

  const lanes = useMemo(() => {
    const defaultLanes = [
      { key: 'weeklyScore', title: 'Weekly Score' },
      { key: 'weeklyVolume', title: `Weekly Volume (${weightUnit}·reps)` },
      { key: 'calories', title: 'Daily Calories' },
      { key: 'protein', title: 'Daily Protein (g)' },
      { key: 'water', title: 'Daily Water (ml)' },
      { key: 'weight', title: `Weight (${weightUnit})` },
      { key: 'bodyFat', title: 'Body Fat Estimate %' },
      { key: 'musculature', title: 'Musculature Index' }
    ];
    if (stats?.customMetrics) {
      const customLanes = Object.keys(stats.customMetrics).sort().map(key => {
        // Sleep is synced in minutes (sleep_minutes -> "Sleep (minutes)") but
        // displayed in hours. Require "min" in the name so e.g. a custom
        // "Sleep quality" metric isn't converted.
        const isSleep = /sleep/i.test(key) && /min/i.test(key);
        return {
          key: `custom-${key}`,
          title: isSleep ? key.replace(/\(min(utes)?\)/i, '(hours)') : key,
          customKey: key,
          isSleep
        };
      });
      return [...defaultLanes, ...customLanes];
    }
    return defaultLanes;
  }, [weightUnit, stats]);

  useEffect(() => {
    if (!chartAreaEl || typeof ResizeObserver === 'undefined') return;

    const ro = new ResizeObserver((entries) => {
      const h = entries?.[0]?.contentRect?.height;
      if (typeof h === 'number' && Number.isFinite(h)) {
        setChartAreaHeight(Math.max(0, Math.floor(h)));
      }
    });

    ro.observe(chartAreaEl);
    return () => ro.disconnect();
  }, [chartAreaEl]);

  useEffect(() => {
    const instance = chartRef.current?.getEchartsInstance?.();
    if (!instance) return;
    instance.resize();
  }, [chartAreaHeight]);

  const processData = (rows, { isRange } = { isRange: false }) => {
    if (!Array.isArray(rows) || rows.length === 0) return [];

    const groups = new Map();

    for (const row of rows) {
      const key = dayKeyMs(row?.date);
      if (!isFiniteNumber(key)) continue;

      const prev = groups.get(key) || { date: key, items: [] };
      prev.items.push(row);
      groups.set(key, prev);
    }

    const processed = [];

    for (const group of groups.values()) {
      const items = group.items || [];
      const count = items.length;
      if (!count) continue;

      const hasAI = items.some((it) => String(it?.source || '').toLowerCase() === 'ai');

      if (isRange) {
        let sumMin = 0;
        let sumMax = 0;
        let n = 0;
        for (const it of items) {
          const min = Number(it?.min);
          const max = Number(it?.max);
          if (!Number.isFinite(min) || !Number.isFinite(max)) continue;
          sumMin += min;
          sumMax += max;
          n += 1;
        }
        if (!n) continue;
        const avgMin = sumMin / n;
        const avgMax = sumMax / n;
        processed.push({
          date: group.date,
          min: avgMin,
          max: avgMax,
          hasAI,
          sourceCount: count
        });
      } else {
        let sum = 0;
        let n = 0;
        // Preserve per-day target values (for nutrition data)
        let targetMin = null;
        let targetMax = null;
        let target = null;
        for (const it of items) {
          const v = Number(it?.value);
          if (!Number.isFinite(v)) continue;
          sum += v;
          n += 1;
          // Use the target from the first item with targets (all items for same day should have same targets)
          if (targetMin === null && it?.targetMin != null) targetMin = it.targetMin;
          if (targetMax === null && it?.targetMax != null) targetMax = it.targetMax;
          if (target === null && it?.target != null) target = it.target;
        }
        if (!n) continue;
        const entry = {
          date: group.date,
          value: sum / n,
          hasAI,
          sourceCount: count
        };
        // Only add target fields if they exist
        if (targetMin !== null) entry.targetMin = targetMin;
        if (targetMax !== null) entry.targetMax = targetMax;
        if (target !== null) entry.target = target;
        processed.push(entry);
      }
    }

    processed.sort((a, b) => a.date - b.date);
    return processed;
  };

  useEffect(() => {
    const loadStats = async () => {
      try {
        const data = await backendService.fetchStats();
        
        const processedCustom = {};
        if (data.customMetrics) {
            Object.keys(data.customMetrics).forEach(k => {
                processedCustom[k] = processData(data.customMetrics[k], { isRange: false });
            });
        }

        // Process nutrition history data
        const nutritionHistory = data.nutritionHistory || { calories: [], protein: [], water: [], targets: {} };
        const caloriesData = processData(nutritionHistory.calories, { isRange: false });
        const proteinData = processData(nutritionHistory.protein, { isRange: false });
        const waterData = processData(nutritionHistory.water, { isRange: false });

        const processedStats = {
            weight: processData(data.weight, { isRange: false }),
            bodyFat: processData(data.bodyFat, { isRange: true }),
            musculature: processData(data.musculature, { isRange: false }),
            weeklyVolume: data.weeklyVolume,
            weeklyScore: data.weeklyScore || [],
            customMetrics: processedCustom,
            calories: caloriesData,
            protein: proteinData,
            water: waterData,
            nutritionTargets: nutritionHistory.targets || {}
        };
        setStats(processedStats);
      } catch (error) {
        console.error("Failed to load stats", error);
      } finally {
        setLoading(false);
      }
    };
    loadStats();
  }, [backendService]);

  const normalizedStats = useMemo(() => {
    if (!stats) return null;

    const GAP_THRESHOLD_MS = 365 * 24 * 60 * 60 * 1000; // 1 year

    const addTrend = (data, { isRange } = { isRange: false }) => {
      if (!Array.isArray(data) || data.length === 0) return [];
      const sorted = [...data].sort((a, b) => a.date - b.date);
      const window = 40;

      return sorted.map((item, index) => {
        let sum = 0;
        let count = 0;
        const start = Math.max(0, index - Math.floor(window / 2));
        const end = Math.min(sorted.length, index + Math.floor(window / 2) + 1);

        for (let i = start; i < end; i++) {
          const it = sorted[i];
          if (isRange) {
            if (!Number.isFinite(it?.min) || !Number.isFinite(it?.max)) continue;
            sum += (it.min + it.max) / 2;
          } else {
            if (!Number.isFinite(it?.value)) continue;
            sum += it.value;
          }
          count += 1;
        }

        return { ...item, trend: count ? sum / count : null };
      });
    };

    const processDataset = (data, { isRange } = { isRange: false }) => {
      if (!Array.isArray(data) || data.length === 0) return [];
      const sorted = [...data].sort((a, b) => a.date - b.date);
      const result = [];

      for (let i = 0; i < sorted.length; i++) {
        if (i > 0) {
          const prev = sorted[i - 1];
          const curr = sorted[i];
          if (curr.date - prev.date > GAP_THRESHOLD_MS) {
            const gapDate = prev.date + Math.floor((curr.date - prev.date) / 2);
            result.push(isRange
              ? { date: gapDate, min: null, max: null, trend: null, hasAI: false, sourceCount: 0, _gap: true }
              : { date: gapDate, value: null, trend: null, hasAI: false, sourceCount: 0, _gap: true }
            );
          }
        }
        result.push(sorted[i]);
      }
      return result;
    };

    const weight = processDataset(stats.weight, { isRange: false });
    const bodyFat = processDataset(addTrend(stats.bodyFat, { isRange: true }), { isRange: true });
    const musculature = processDataset(addTrend(stats.musculature, { isRange: false }), { isRange: false });
    const weeklyVolume = processDataset(stats.weeklyVolume, { isRange: false });
    const weeklyScore = processDataset(stats.weeklyScore || [], { isRange: false });
    
    // Nutrition data
    const calories = processDataset(stats.calories || [], { isRange: false });
    const protein = processDataset(stats.protein || [], { isRange: false });
    const water = processDataset(stats.water || [], { isRange: false });

    // Custom Metrics
    const customMetrics = {};
    if (stats.customMetrics) {
        Object.keys(stats.customMetrics).forEach(key => {
            customMetrics[key] = processDataset(stats.customMetrics[key], { isRange: false });
        });
    }

    const allDatesSet = new Set();
    [weight, bodyFat, musculature, weeklyVolume, weeklyScore, calories, protein, water].forEach(ds => {
        ds.forEach(d => allDatesSet.add(d.date));
    });
    // Add custom metric dates
    Object.values(customMetrics).forEach(ds => {
        ds.forEach(d => allDatesSet.add(d.date));
    });

    return {
        weight,
        bodyFat,
        musculature,
        weeklyVolume,
        weeklyScore,
        calories,
        protein,
        water,
        nutritionTargets: stats.nutritionTargets || {},
        customMetrics,
        allDates: Array.from(allDatesSet).sort((a, b) => a - b)
    };
  }, [stats]);

  const chartModel = useMemo(() => {
    if (!normalizedStats) return null;

    const weightPoints = (normalizedStats.weight || []).map((d) => {
      const v = d?.value;
      const display = (v !== null && v !== undefined && weightUnit === 'kg') ? lbsToKg(v) : v;
      return {
        t: d.date,
        v: isFiniteNumber(display) ? display : null,
        hasAI: Boolean(d?.hasAI)
      };
    });

    const weeklyVolumePoints = (normalizedStats.weeklyVolume || []).map((d) => {
      const v = d?.value;
      const display = (v !== null && v !== undefined && weightUnit === 'kg') ? lbsToKg(v) : v;
      return {
        t: d.date,
        v: isFiniteNumber(display) ? display : null
      };
    });

    // Score is unitless and not weight-dependent for display purposes — it's an
    // Epley-derived metric so we don't convert between lb/kg.
    const weeklyScorePoints = (normalizedStats.weeklyScore || []).map((d) => ({
      t: d.date,
      v: isFiniteNumber(d?.value) ? d.value : null
    }));

    // Nutrition points - include per-day targets for accurate historical representation
    const caloriesPoints = (normalizedStats.calories || []).map((d) => ({
      t: d.date,
      v: isFiniteNumber(d?.value) ? d.value : null,
      targetMin: d?.targetMin,
      targetMax: d?.targetMax,
      target: d?.target
    }));
    
    const proteinPoints = (normalizedStats.protein || []).map((d) => ({
      t: d.date,
      v: isFiniteNumber(d?.value) ? d.value : null,
      target: d?.target
    }));

    const waterPoints = (normalizedStats.water || []).map((d) => ({
      t: d.date,
      v: isFiniteNumber(d?.value) ? d.value : null,
      target: d?.target
    }));
    
    const nutritionTargets = normalizedStats.nutritionTargets || {};

    const musculaturePoints = (normalizedStats.musculature || []).map((d) => ({
      t: d.date,
      v: isFiniteNumber(d?.value) ? d.value : null,
      trend: isFiniteNumber(d?.trend) ? d.trend : null,
      hasAI: Boolean(d?.hasAI)
    }));

    const bodyFatPoints = (normalizedStats.bodyFat || []).map((d) => {
      const min = isFiniteNumber(d?.min) ? d.min : null;
      const max = isFiniteNumber(d?.max) ? d.max : null;
      const mid = (min !== null && max !== null) ? (min + max) / 2 : null;
      const trend = isFiniteNumber(d?.trend) ? d.trend : null;
      return {
        t: d.date,
        min,
        max,
        mid,
        trend,
        hasAI: Boolean(d?.hasAI)
      };
    });

    const allDates = normalizedStats.allDates || [];
    const minDate = allDates.length ? Math.min(...allDates) : null;
    const maxDate = allDates.length ? Math.max(...allDates) : null;

    const customPoints = {};
    if (normalizedStats.customMetrics) {
        Object.keys(normalizedStats.customMetrics).forEach(key => {
            customPoints[key] = (normalizedStats.customMetrics[key] || []).map(d => ({
                t: d.date,
                v: isFiniteNumber(d?.value) ? d.value : null
            }));
        });
    }

    // Initial zoom shows last 3 months (or all data if less)
    const initialEnd = maxDate;
    const initialStart = (() => {
      if (!isFiniteNumber(maxDate) || !isFiniteNumber(minDate)) return null;
      const threeMonths = RANGES.THREE_MONTHS;
      const desired = maxDate - threeMonths;
      return Math.max(minDate, desired);
    })();

    return {
      minDate,
      maxDate,
      zoomStartValue: initialStart,
      zoomEndValue: initialEnd,
      weeklyVolumePoints,
      weeklyScorePoints,
      caloriesPoints,
      proteinPoints,
      waterPoints,
      nutritionTargets,
      weightPoints,
      bodyFatPoints,
      musculaturePoints,
      customPoints
    };
  }, [normalizedStats, weightUnit]);

  const chartData = useMemo(() => {
    if (!chartModel) return null;

    const {
      minDate,
      maxDate,
      zoomStartValue: initialZoomStart,
      zoomEndValue: initialZoomEnd,
      weeklyVolumePoints,
      weeklyScorePoints,
      caloriesPoints,
      proteinPoints,
      waterPoints,
      nutritionTargets,
      weightPoints,
      bodyFatPoints,
      musculaturePoints,
      customPoints
    } = chartModel;

    // Use zoomRange state if set, otherwise fall back to chartModel values
    const zoomStartValue = zoomRange.start ?? initialZoomStart;
    const zoomEndValue = zoomRange.end ?? initialZoomEnd;

    const hasAny = [weeklyVolumePoints, weeklyScorePoints, caloriesPoints, proteinPoints, waterPoints, weightPoints, bodyFatPoints, musculaturePoints, ...Object.values(customPoints || {})]
      .some((arr) => Array.isArray(arr) && arr.some((p) => isFiniteNumber(p?.v) || isFiniteNumber(p?.mid) || isFiniteNumber(p?.trend)));

    if (!hasAny) return null;

    const AXIS_TEXT = 'rgba(226,232,240,0.78)';
    const GRID_LINE = 'rgba(255,255,255,0.06)';

    // Colors aligned with the workout exercise history chart so users see the
    // same color associations across the app.
    const SCORE_COLOR = '#facc15'; // yellow — primary lifting metric
    const VOLUME_COLOR = '#a855f7'; // purple — secondary lifting metric
    const CALORIES_COLOR = '#f97316'; // Orange for calories
    const PROTEIN_COLOR = '#ec4899'; // Pink for protein
    const CALORIES_TARGET_COLOR = 'rgba(249, 115, 22, 0.36)';
    const PROTEIN_TARGET_COLOR = 'rgba(236, 72, 153, 0.7)';
    const WATER_COLOR = '#38bdf8'; // Sky blue for water
    const WATER_TARGET_COLOR = 'rgba(56, 189, 248, 0.7)';
    const SLEEP_TARGET_HOURS = 7;
    const SLEEP_OK_COLOR = '#34d399'; // green — target reached
    const SLEEP_UNDER_COLOR = '#f87171'; // red — under target
    const SLEEP_TARGET_COLOR = 'rgba(129, 140, 248, 0.7)'; // indigo dashed target line
    const WEIGHT_COLOR = '#38bdf8';
    const BODYFAT_COLOR = '#34d399';
    const MUSCLE_COLOR = '#fbbf24';
    const TREND_DARK_GREEN = '#059669';
    const TREND_ORANGE = '#d97706';
    const CURVE_WIDTH = 1.5;

    // Look up axis indices by lane key so series wiring stays correct
    // regardless of how lanes are reordered in `lanes` above.
    const laneIndex = (key) => lanes.findIndex((l) => l.key === key);
    const SCORE_LANE = laneIndex('weeklyScore');
    const VOLUME_LANE = laneIndex('weeklyVolume');
    const CALORIES_LANE = laneIndex('calories');
    const PROTEIN_LANE = laneIndex('protein');
    const WATER_LANE = laneIndex('water');
    const WEIGHT_LANE = laneIndex('weight');
    const BODYFAT_LANE = laneIndex('bodyFat');
    const MUSCLE_LANE = laneIndex('musculature');

    // Build background bands for calories outside target range.
    // Under-target periods are yellow; over-target periods are red.
    const caloriesOutOfBudgetMarkAreas = (() => {
      const DAY_MS = 24 * 60 * 60 * 1000;
      const HALF_DAY_MS = 12 * 60 * 60 * 1000;
      const sorted = [...caloriesPoints]
        .filter((p) => isFiniteNumber(p?.t) && isFiniteNumber(p?.v))
        .sort((a, b) => a.t - b.t);
      if (!sorted.length) return { under: [], over: [] };

      const underWindows = [];
      const overWindows = [];
      for (const p of sorted) {
        const min = isFiniteNumber(p?.targetMin) ? p.targetMin : (nutritionTargets?.calories?.min ?? 1800);
        const max = isFiniteNumber(p?.targetMax) ? p.targetMax : (nutritionTargets?.calories?.max ?? 2200);
        if (!isFiniteNumber(min) || !isFiniteNumber(max)) continue;
        if (p.v < min) {
          underWindows.push({ start: p.t - HALF_DAY_MS, end: p.t + HALF_DAY_MS });
        } else if (p.v > max) {
          overWindows.push({ start: p.t - HALF_DAY_MS, end: p.t + HALF_DAY_MS });
        }
      }

      const mergeWindows = (windows) => {
        if (!windows.length) return [];

        // Merge contiguous windows so multi-day streaks show as a single band.
        const merged = [];
        for (const w of windows) {
          const prev = merged[merged.length - 1];
          if (!prev) {
            merged.push({ ...w });
            continue;
          }
          if (w.start <= (prev.end + DAY_MS * 0.2)) {
            prev.end = Math.max(prev.end, w.end);
          } else {
            merged.push({ ...w });
          }
        }
        return merged.map((w) => [{ xAxis: w.start }, { xAxis: w.end }]);
      };

      return {
        under: mergeWindows(underWindows),
        over: mergeWindows(overWindows)
      };
    })();

    const proteinUnderTargetMarkAreas = (() => {
      const DAY_MS = 24 * 60 * 60 * 1000;
      const HALF_DAY_MS = 12 * 60 * 60 * 1000;
      const sorted = [...proteinPoints]
        .filter((p) => isFiniteNumber(p?.t) && isFiniteNumber(p?.v))
        .sort((a, b) => a.t - b.t);
      if (!sorted.length) return [];

      const underWindows = [];
      for (const p of sorted) {
        const threshold = isFiniteNumber(p?.target)
          ? p.target
          : (nutritionTargets?.protein?.target ?? nutritionTargets?.protein?.min ?? 120);
        if (!isFiniteNumber(threshold)) continue;
        if (p.v < threshold) {
          underWindows.push({ start: p.t - HALF_DAY_MS, end: p.t + HALF_DAY_MS });
        }
      }

      if (!underWindows.length) return [];

      const merged = [];
      for (const w of underWindows) {
        const prev = merged[merged.length - 1];
        if (!prev) {
          merged.push({ ...w });
          continue;
        }
        if (w.start <= (prev.end + DAY_MS * 0.2)) {
          prev.end = Math.max(prev.end, w.end);
        } else {
          merged.push({ ...w });
        }
      }

      return merged.map((w) => [{ xAxis: w.start }, { xAxis: w.end }]);
    })();

    // Water under-target windows (mirrors protein logic)
    const waterUnderTargetMarkAreas = (() => {
      const DAY_MS = 24 * 60 * 60 * 1000;
      const HALF_DAY_MS = 12 * 60 * 60 * 1000;
      const sorted = [...waterPoints]
        .filter((p) => isFiniteNumber(p?.t) && isFiniteNumber(p?.v))
        .sort((a, b) => a.t - b.t);
      if (!sorted.length) return [];

      const underWindows = [];
      for (const p of sorted) {
        const threshold = isFiniteNumber(p?.target)
          ? p.target
          : (nutritionTargets?.water?.target ?? nutritionTargets?.water?.min ?? 0);
        if (!isFiniteNumber(threshold) || threshold <= 0) continue;
        if (p.v < threshold) {
          underWindows.push({ start: p.t - HALF_DAY_MS, end: p.t + HALF_DAY_MS });
        }
      }

      if (!underWindows.length) return [];

      const merged = [];
      for (const w of underWindows) {
        const prev = merged[merged.length - 1];
        if (!prev) {
          merged.push({ ...w });
          continue;
        }
        if (w.start <= (prev.end + DAY_MS * 0.2)) {
          prev.end = Math.max(prev.end, w.end);
        } else {
          merged.push({ ...w });
        }
      }

      return merged.map((w) => [{ xAxis: w.start }, { xAxis: w.end }]);
    })();

    const formatK = (num) => {
      const n = Number(num);
      if (!Number.isFinite(n)) return '';
      const abs = Math.abs(n);
      if (abs < 1000) return String(Math.round(n));
      const scaled = n / 1000;
      const decimals = Math.abs(scaled) >= 10 ? 0 : 1;
      const text = scaled.toFixed(decimals).replace(/\.0$/, '');
      return `${text}k`;
    };

    const left = 58;
    const right = 18;

    // Calculate height based on number of lanes to ensure each has enough space
    const MIN_LANE_HEIGHT = 100;
    const LANE_GAP = 52;
    const gridTop = LANE_GAP + 2;
    const bottomPadding = 12;
    
    // Calculate container height to fit all lanes comfortably
    const containerHeight = gridTop + (lanes.length * MIN_LANE_HEIGHT) + ((lanes.length - 1) * LANE_GAP) + bottomPadding;

    const laneTitleHeight = 18;
    const titleNudgeDown = 2;

    // Simple lane layout - each lane gets MIN_LANE_HEIGHT with LANE_GAP between
    const laneHeight = MIN_LANE_HEIGHT;
    const laneGap = LANE_GAP;

    const grid = lanes.map((_, idx) => ({
      left,
      right,
      top: gridTop + idx * (laneHeight + laneGap),
      height: laneHeight,
      containLabel: false
    }));

    const xAxis = lanes.map((lane, idx) => ({
      type: 'time',
      gridIndex: idx,
      min: isFiniteNumber(zoomStartValue) ? zoomStartValue : (isFiniteNumber(minDate) ? minDate : 'dataMin'),
      max: isFiniteNumber(zoomEndValue) ? zoomEndValue : (isFiniteNumber(maxDate) ? maxDate : 'dataMax'),
      splitNumber: 4,
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: true, lineStyle: { color: GRID_LINE, width: 1 } },
      axisLabel: { show: false }
    }));

    const yAxis = lanes.map((lane, idx) => {
      const common = {
        type: 'value',
        gridIndex: idx,
        splitNumber: 2,
        min: 'dataMin', // Auto-adjust lower bound to data points
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: true, lineStyle: { color: GRID_LINE, width: 1 } }
      };

      if (lane.key === 'weeklyScore') {
        return {
          ...common,
          axisLabel: {
            color: SCORE_COLOR,
            hideOverlap: true,
            formatter: (v) => formatK(v)
          }
        };
      }
      if (lane.key === 'weeklyVolume') {
        return {
          ...common,
          axisLabel: {
            color: VOLUME_COLOR,
            hideOverlap: true,
            formatter: (v) => formatK(v)
          }
        };
      }
      if (lane.key === 'calories') {
        return {
          ...common,
          axisLabel: {
            color: CALORIES_COLOR,
            hideOverlap: true,
            formatter: (v) => formatK(v)
          }
        };
      }
      if (lane.key === 'protein') {
        return {
          ...common,
          axisLabel: {
            color: PROTEIN_COLOR,
            hideOverlap: true,
            formatter: (v) => (Number.isFinite(Number(v)) ? String(Math.round(Number(v))) : '')
          }
        };
      }
      if (lane.key === 'water') {
        return {
          ...common,
          axisLabel: {
            color: WATER_COLOR,
            hideOverlap: true,
            formatter: (v) => formatK(v)
          }
        };
      }
      if (lane.key === 'weight') {
        return {
          ...common,
          axisLabel: {
            color: WEIGHT_COLOR,
            hideOverlap: true,
            formatter: (v) => (Number.isFinite(Number(v)) ? String(Math.round(Number(v))) : '')
          }
        };
      }
      if (lane.key === 'bodyFat') {
        return {
          ...common,
          axisLabel: {
            color: BODYFAT_COLOR,
            hideOverlap: true,
            formatter: (v) => (Number.isFinite(Number(v)) ? `${Math.round(Number(v) * 100)}%` : '')
          }
        };
      }

      if (lane.customKey) {
        if (lane.isSleep) {
          return {
            ...common,
            // Bars render from the axis baseline, so start at 0; keep the 7h
            // target line in view even when every night is below it.
            min: 0,
            max: ({ max }) => Math.ceil(Math.max(max, SLEEP_TARGET_HOURS + 1)),
            axisLabel: {
              color: AXIS_TEXT,
              hideOverlap: true,
              formatter: (v) => (Number.isFinite(Number(v)) ? `${Number(v)}h` : '')
            }
          };
        }
        return {
          ...common,
          axisLabel: {
            color: AXIS_TEXT,
            hideOverlap: true,
            formatter: (v) => (Number.isFinite(Number(v)) ? String(Number(v).toFixed(1)) : '')
          }
        };
      }

      return {
        ...common,
        // Keep the lowest reading above the floor (formatted 5.8 would read as the "6" floor).
        min: ({ min }) => Math.max(0, Math.ceil(min) - 1),
        max: 10,
        axisLabel: {
          color: MUSCLE_COLOR,
          hideOverlap: true,
          formatter: (v) => (Number.isFinite(Number(v)) ? String(Number(v).toFixed(0)) : '')
        }
      };
    });

    const series = [];

    // Weekly Score lane — Epley-based primary metric, mirrors workout exercise charts.
    // Mark the last week as partial with a white pulsing bar (animation handled via useEffect).
    const lastScoreIdx = weeklyScorePoints.length - 1;
    series.push({
      id: 'weekly-score',
      name: 'Weekly Score',
      type: 'bar',
      xAxisIndex: SCORE_LANE,
      yAxisIndex: SCORE_LANE,
      data: weeklyScorePoints.map((p, idx) => {
        const isPartialWeek = idx === lastScoreIdx && lastScoreIdx >= 0;
        return {
          value: [p.t, p.v],
          isPartialWeek,
          itemStyle: isPartialWeek
            ? { color: 'rgba(255, 255, 255, 1)' }
            : { color: SCORE_COLOR }
        };
      }),
      barMaxWidth: 14,
      tooltip: { valueFormatter: (v) => (Number.isFinite(Number(v)) ? formatK(v) : '-') }
    });

    // Weekly Volume lane
    // Mark the last week as partial with white pulsing bar (animation handled via useEffect)
    const lastVolumeIdx = weeklyVolumePoints.length - 1;
    series.push({
      id: 'weekly-volume',
      name: `Weekly Volume (${weightUnit}·reps)`,
      type: 'bar',
      xAxisIndex: VOLUME_LANE,
      yAxisIndex: VOLUME_LANE,
      data: weeklyVolumePoints.map((p, idx) => {
        const isPartialWeek = idx === lastVolumeIdx && lastVolumeIdx >= 0;
        return {
          value: [p.t, p.v],
          isPartialWeek,
          itemStyle: isPartialWeek 
            ? { color: 'rgba(255, 255, 255, 1)' }
            : { color: VOLUME_COLOR }
        };
      }),
      barMaxWidth: 14,
      tooltip: { valueFormatter: (v) => (Number.isFinite(Number(v)) ? formatK(v) : '-') }
    });

    // Calories lane (index 1) - with per-day target range band
    // Build per-day target data for accurate historical representation
    const caloriesTargetMinData = caloriesPoints.map(p => [p.t, p.targetMin || nutritionTargets?.calories?.min || 1800]);
    const caloriesTargetRangeData = caloriesPoints.map(p => {
      const min = p.targetMin || nutritionTargets?.calories?.min || 1800;
      const max = p.targetMax || nutritionTargets?.calories?.max || 2200;
      return [p.t, max - min];
    });
    
    // Target range band for calories (stacked area)
    series.push({
      id: 'calories-target-min',
      name: '_Calories Target Min',
      type: 'line',
      xAxisIndex: CALORIES_LANE,
      yAxisIndex: CALORIES_LANE,
      data: caloriesTargetMinData,
      showSymbol: false,
      lineStyle: { opacity: 0 },
      areaStyle: { opacity: 0 },
      stack: 'calories-target',
      tooltip: { show: false }
    });
    series.push({
      id: 'calories-target-range',
      name: '_Calories Target Range',
      type: 'line',
      xAxisIndex: CALORIES_LANE,
      yAxisIndex: CALORIES_LANE,
      data: caloriesTargetRangeData,
      showSymbol: false,
      lineStyle: { opacity: 0 },
      areaStyle: { color: CALORIES_TARGET_COLOR, opacity: 1 },
      stack: 'calories-target',
      tooltip: { show: false }
    });
    series.push({
      id: 'calories',
      name: 'Daily Calories',
      type: 'line',
      xAxisIndex: CALORIES_LANE,
      yAxisIndex: CALORIES_LANE,
      data: caloriesPoints.map((p) => [p.t, p.v]),
      showSymbol: false,
      smooth: false,
      lineStyle: { width: CURVE_WIDTH, color: CALORIES_COLOR },
      itemStyle: { color: CALORIES_COLOR },
      connectNulls: false,
      markArea: (caloriesOutOfBudgetMarkAreas.under.length > 0 || caloriesOutOfBudgetMarkAreas.over.length > 0) ? {
        silent: true,
        data: [
          ...caloriesOutOfBudgetMarkAreas.under.map((pair) => ([
            { ...pair[0], itemStyle: { color: 'rgba(234, 179, 8, 0.2)' } },
            pair[1]
          ])),
          ...caloriesOutOfBudgetMarkAreas.over.map((pair) => ([
            { ...pair[0], itemStyle: { color: 'rgba(239, 68, 68, 0.16)' } },
            pair[1]
          ]))
        ]
      } : undefined
    });

    // Protein lane - with per-day minimum target line
    const defaultProteinTarget = nutritionTargets?.protein?.target || nutritionTargets?.protein?.min || 120;
    const proteinTargetData = proteinPoints.map(p => [p.t, p.target || defaultProteinTarget]);
    
    // Per-day target line for protein minimum
    series.push({
      id: 'protein-target-line',
      name: 'Protein Min Target',
      type: 'line',
      xAxisIndex: PROTEIN_LANE,
      yAxisIndex: PROTEIN_LANE,
      data: proteinTargetData,
      showSymbol: false,
      lineStyle: { width: 1, type: 'dashed', color: PROTEIN_TARGET_COLOR },
      tooltip: { show: false }
    });
    series.push({
      id: 'protein',
      name: 'Daily Protein (g)',
      type: 'line',
      xAxisIndex: PROTEIN_LANE,
      yAxisIndex: PROTEIN_LANE,
      data: proteinPoints.map((p) => [p.t, p.v]),
      showSymbol: false,
      smooth: false,
      lineStyle: { width: CURVE_WIDTH, color: PROTEIN_COLOR },
      itemStyle: { color: PROTEIN_COLOR },
      connectNulls: false,
      markArea: proteinUnderTargetMarkAreas.length > 0 ? {
        silent: true,
        itemStyle: {
          color: 'rgba(239, 68, 68, 0.16)'
        },
        data: proteinUnderTargetMarkAreas
      } : undefined
    });

    // Water lane — with per-day minimum target line, mirroring protein.
    const defaultWaterTarget = nutritionTargets?.water?.target || nutritionTargets?.water?.min || 0;
    const waterTargetData = waterPoints.map(p => [p.t, p.target || defaultWaterTarget]);
    if (waterTargetData.some(([, v]) => Number.isFinite(v) && v > 0)) {
      series.push({
        id: 'water-target-line',
        name: 'Water Min Target',
        type: 'line',
        xAxisIndex: WATER_LANE,
        yAxisIndex: WATER_LANE,
        data: waterTargetData,
        showSymbol: false,
        lineStyle: { width: 1, type: 'dashed', color: WATER_TARGET_COLOR },
        tooltip: { show: false }
      });
    }
    series.push({
      id: 'water',
      name: 'Daily Water (ml)',
      type: 'line',
      xAxisIndex: WATER_LANE,
      yAxisIndex: WATER_LANE,
      data: waterPoints.map((p) => [p.t, p.v]),
      showSymbol: false,
      smooth: false,
      lineStyle: { width: CURVE_WIDTH, color: WATER_COLOR },
      itemStyle: { color: WATER_COLOR },
      connectNulls: false,
      markArea: waterUnderTargetMarkAreas.length > 0 ? {
        silent: true,
        itemStyle: {
          color: 'rgba(239, 68, 68, 0.16)'
        },
        data: waterUnderTargetMarkAreas
      } : undefined
    });

    // Weight lane
    series.push({
      id: 'weight',
      name: `Weight (${weightUnit})`,
      type: 'line',
      xAxisIndex: WEIGHT_LANE,
      yAxisIndex: WEIGHT_LANE,
      data: weightPoints.map((p) => [p.t, p.v]),
      showSymbol: false,
      smooth: false,
      lineStyle: { width: CURVE_WIDTH, color: WEIGHT_COLOR },
      itemStyle: { color: WEIGHT_COLOR },
      connectNulls: false
    });


    // Body Fat lane: stacked fill between min and max
    const bfMin = bodyFatPoints.map((p) => [p.t, p.min]);
    const bfRange = bodyFatPoints.map((p) => {
      if (!isFiniteNumber(p.min) || !isFiniteNumber(p.max)) return [p.t, null];
      return [p.t, Math.max(0, p.max - p.min)];
    });
    const bfMid = bodyFatPoints.map((p) => [p.t, p.mid]);
    // A line needs two adjacent values: dot isolated points (e.g. one photo-estimate
    // day) so that lane is not drawn empty. Other points keep symbol 'none'.
    const dotIsolated = (data) => data.map(([t, v], i) => (
      isFiniteNumber(v) && !isFiniteNumber(data[i - 1]?.[1]) && !isFiniteNumber(data[i + 1]?.[1])
        ? { value: [t, v], symbol: 'circle', symbolSize: 6 } : [t, v]));
    const bfTrend = bodyFatPoints.map((p) => [p.t, p.trend]);

    series.push({
      id: 'bf-min',
      name: '_Body Fat Min',
      type: 'line',
      xAxisIndex: BODYFAT_LANE,
      yAxisIndex: BODYFAT_LANE,
      data: bfMin,
      stack: 'bodyfat',
      showSymbol: false,
      lineStyle: { opacity: 0 },
      areaStyle: { opacity: 0 },
      tooltip: { show: false }
    });
    series.push({
      id: 'bf-range',
      name: '_Body Fat Range',
      type: 'line',
      xAxisIndex: BODYFAT_LANE,
      yAxisIndex: BODYFAT_LANE,
      data: bfRange,
      stack: 'bodyfat',
      showSymbol: false,
      lineStyle: { opacity: 0 },
      areaStyle: { color: BODYFAT_COLOR, opacity: 0.3 },
      tooltip: { show: false }
    });
    series.push({
      id: 'bf-mid',
      name: 'Body Fat Estimate',
      type: 'line',
      xAxisIndex: BODYFAT_LANE,
      yAxisIndex: BODYFAT_LANE,
      data: dotIsolated(bfMid),
      symbol: 'none',
      lineStyle: { width: CURVE_WIDTH, color: BODYFAT_COLOR },
      itemStyle: { color: BODYFAT_COLOR },
      connectNulls: false
    });
    series.push({
      id: 'bf-trend',
      name: 'Body Fat Estimate Trend',
      type: 'line',
      xAxisIndex: BODYFAT_LANE,
      yAxisIndex: BODYFAT_LANE,
      data: bfTrend,
      showSymbol: false,
      lineStyle: { width: CURVE_WIDTH, color: TREND_DARK_GREEN, type: 'dashed' },
      itemStyle: { color: TREND_DARK_GREEN },
      connectNulls: false
    });

    // Musculature lane (index 5)
    series.push({
      id: 'muscle',
      name: 'Musculature',
      type: 'line',
      xAxisIndex: MUSCLE_LANE,
      yAxisIndex: MUSCLE_LANE,
      data: dotIsolated(musculaturePoints.map((p) => [p.t, p.v])),
      symbol: 'none',
      lineStyle: { width: CURVE_WIDTH, color: MUSCLE_COLOR },
      itemStyle: { color: MUSCLE_COLOR },
      connectNulls: false
    });
    series.push({
      id: 'muscle-trend',
      name: 'Musculature Trend',
      type: 'line',
      xAxisIndex: MUSCLE_LANE,
      yAxisIndex: MUSCLE_LANE,
      data: musculaturePoints.map((p) => [p.t, p.trend]),
      showSymbol: false,
      lineStyle: { width: CURVE_WIDTH, color: TREND_ORANGE, type: 'dashed' },
      itemStyle: { color: TREND_ORANGE },
      connectNulls: false
    });

    if (customPoints) {
        const CUSTOM_COLORS = ['#f472b6', '#22d3ee', '#a3e635', '#facc15']; 
        lanes.forEach((lane, idx) => {
             if (!lane.customKey) return;
             
             const rawPoints = customPoints[lane.customKey] || [];
             // Sleep is stored in minutes; plot in hours.
             const points = lane.isSleep
               ? rawPoints.map((p) => ({ t: p.t, v: isFiniteNumber(p.v) ? p.v / 60 : null }))
               : rawPoints;
             const color = CUSTOM_COLORS[idx % CUSTOM_COLORS.length];

             if (lane.isSleep) {
               // Per-day bars, each colored green when the 7h target is
               // reached and red when under. (A piecewise visualMap on a line
               // series crashes ECharts' LineView when the data contains the
               // null gap points we insert, so color per bar instead.)
               series.push({
                 id: `custom-${lane.customKey}`,
                 name: lane.title,
                 type: 'bar',
                 xAxisIndex: idx,
                 yAxisIndex: idx,
                 data: points.map((p) => ({
                   value: [p.t, p.v],
                   itemStyle: {
                     color: isFiniteNumber(p.v) && p.v >= SLEEP_TARGET_HOURS
                       ? SLEEP_OK_COLOR
                       : SLEEP_UNDER_COLOR
                   }
                 })),
                 barMaxWidth: 14,
                 markLine: {
                   silent: true,
                   symbol: 'none',
                   data: [{ yAxis: SLEEP_TARGET_HOURS }],
                   lineStyle: { width: 1, type: 'dashed', color: SLEEP_TARGET_COLOR },
                   label: { show: false }
                 }
               });
               return;
             }

             series.push({
                 id: `custom-${lane.customKey}`,
                 name: lane.title,
                 type: 'line',
                 xAxisIndex: idx,
                 yAxisIndex: idx,
                 data: points.map(p => [p.t, p.v]),
                 showSymbol: false,
                 lineStyle: { width: CURVE_WIDTH, color },
                 itemStyle: { color },
                 connectNulls: false
             });
        });
    }

    const tooltipFormatter = (params) => {
      const items = Array.isArray(params) ? params : [params];
      const t = items[0]?.axisValue;
      const dateLabel = t ? formatDate(Number(t)) : '';

      const bfMinItem = items.find((it) => it?.seriesId === 'bf-min');
      const bfRangeItem = items.find((it) => it?.seriesId === 'bf-range');

      const bfMinVal = Array.isArray(bfMinItem?.value) ? bfMinItem.value[1] : null;
      const bfRangeVal = Array.isArray(bfRangeItem?.value) ? bfRangeItem.value[1] : null;
      const bfMaxVal = (isFiniteNumber(bfMinVal) && isFiniteNumber(bfRangeVal)) ? (bfMinVal + bfRangeVal) : null;

      const lines = [];
      if (dateLabel) {
        lines.push(`<div style="font-weight:600; margin-bottom:4px;">${cssEscape(dateLabel)}</div>`);
      }

      const pushLine = (label, color, valueText) => {
        if (!valueText) return;
        lines.push(
          `<div style="display:flex; gap:8px; align-items:baseline;">` +
          `<span style="display:inline-block; width:8px; height:8px; border-radius:999px; background:${color}; margin-top:2px;"></span>` +
          `<span style="opacity:0.92;">${cssEscape(label)}:</span>` +
          `<span style="font-weight:600;">${cssEscape(valueText)}</span>` +
          `</div>`
        );
      };

      const pick = (id) => items.find((it) => it?.seriesId === id);
      const pickValue = (id) => {
        const it = pick(id);
        const val = Array.isArray(it?.value) ? it.value[1] : it?.value;
        return Number(val);
      };

      const hoveredTimestamp = Number(t);
      const hoveredCaloriesPoint = caloriesPoints.find((point) => Number(point?.t) === hoveredTimestamp) || null;
      const hoveredProteinPoint = proteinPoints.find((point) => Number(point?.t) === hoveredTimestamp) || null;
      const hoveredWaterPoint = waterPoints.find((point) => Number(point?.t) === hoveredTimestamp) || null;

      const hasSeries = (id) => items.some((it) => it?.seriesId === id);

      if (hasSeries('weekly-score')) {
        const scoreItem = pick('weekly-score');
        const sc = pickValue('weekly-score');
        const isPartialWeek = scoreItem?.data?.isPartialWeek;
        if (Number.isFinite(sc)) {
          const label = isPartialWeek
            ? 'Weekly Score – partial week'
            : 'Weekly Score';
          const color = isPartialWeek ? '#ffffff' : SCORE_COLOR;
          pushLine(label, color, String(Math.round(sc)));
        }
      } else if (hasSeries('weekly-volume')) {
        const volumeItem = pick('weekly-volume');
        const vol = pickValue('weekly-volume');
        const isPartialWeek = volumeItem?.data?.isPartialWeek;
        if (Number.isFinite(vol)) {
          const label = isPartialWeek
            ? `Weekly Volume (${weightUnit}·reps) – partial week`
            : `Weekly Volume (${weightUnit}·reps)`;
          const color = isPartialWeek ? '#ffffff' : VOLUME_COLOR;
          pushLine(label, color, String(Math.round(vol)));
        }
      } else if (hasSeries('calories')) {
        const calories = pickValue('calories');
        if (Number.isFinite(calories)) {
          pushLine('Daily Calories', CALORIES_COLOR, `${Math.round(calories)}`);
        }
        const min = Number(hoveredCaloriesPoint?.targetMin);
        const max = Number(hoveredCaloriesPoint?.targetMax);
        if (Number.isFinite(min) && Number.isFinite(max)) {
          pushLine('Budget', CALORIES_TARGET_COLOR, `${Math.round(min)}-${Math.round(max)}`);
        }
      } else if (hasSeries('protein')) {
        const protein = pickValue('protein');
        if (Number.isFinite(protein)) {
          pushLine('Daily Protein (g)', PROTEIN_COLOR, `${Math.round(protein)}`);
        }
        const proteinTarget = Number(hoveredProteinPoint?.target);
        if (Number.isFinite(proteinTarget)) {
          pushLine('Target', PROTEIN_TARGET_COLOR, `${Math.round(proteinTarget)}g`);
        }
      } else if (hasSeries('water') || hasSeries('water-target-line')) {
        const water = pickValue('water');
        if (Number.isFinite(water)) {
          pushLine('Daily Water', WATER_COLOR, `${Math.round(water)} ml`);
        }
        const waterTarget = Number(hoveredWaterPoint?.target) || Number(nutritionTargets?.water?.target) || Number(nutritionTargets?.water?.min) || null;
        if (Number.isFinite(waterTarget) && waterTarget > 0) {
          pushLine('Target', WATER_TARGET_COLOR, `${Math.round(waterTarget)} ml`);
        }
      } else if (hasSeries('weight')) {
        const wt = pickValue('weight');
        if (Number.isFinite(wt)) {
          pushLine(`Weight (${weightUnit})`, WEIGHT_COLOR, `${wt.toFixed(1)}`);
        }
      } else if (hasSeries('bf-mid') || hasSeries('bf-min') || hasSeries('bf-range')) {
        if (isFiniteNumber(bfMinVal) && isFiniteNumber(bfMaxVal)) {
          pushLine('Body Fat Estimate', BODYFAT_COLOR, `${(bfMinVal * 100).toFixed(1)}% – ${(bfMaxVal * 100).toFixed(1)}%`);
        } else {
          const bfMidVal = pickValue('bf-mid');
          if (Number.isFinite(bfMidVal)) {
            pushLine('Body Fat Estimate', BODYFAT_COLOR, `${(bfMidVal * 100).toFixed(1)}%`);
          }
        }
      } else if (hasSeries('muscle')) {
        const mus = pickValue('muscle');
        if (Number.isFinite(mus)) {
          pushLine('Musculature', MUSCLE_COLOR, mus.toFixed(1));
        }
      } else if (customPoints) {
        const CUSTOM_COLORS = ['#f472b6', '#22d3ee', '#a3e635', '#facc15'];
        lanes.forEach((lane, idx) => {
          if (!lane.customKey || !hasSeries(`custom-${lane.customKey}`)) return;
          const pt = pickValue(`custom-${lane.customKey}`);
          const color = CUSTOM_COLORS[idx % CUSTOM_COLORS.length];
          if (Number.isFinite(pt)) {
            if (lane.isSleep) {
              // pt is already in hours (converted at series build time).
              let h = Math.floor(pt);
              let m = Math.round((pt - h) * 60);
              if (m === 60) { h += 1; m = 0; }
              const reached = pt >= SLEEP_TARGET_HOURS;
              pushLine('Sleep', reached ? SLEEP_OK_COLOR : SLEEP_UNDER_COLOR, `${h}h ${String(m).padStart(2, '0')}m`);
              pushLine('Target', SLEEP_TARGET_COLOR, `${SLEEP_TARGET_HOURS}h`);
            } else {
              pushLine(lane.title, color, pt.toFixed(1));
            }
          }
        });
      }

      return lines.join('');
    };

    const opt = {
      animation: false,
      grid,
      xAxis,
      yAxis,
      axisPointer: {
        snap: true,
        label: { backgroundColor: 'rgba(0,0,0,0.35)' }
      },
      tooltip: {
        trigger: 'axis',
        confine: true,
        backgroundColor: 'rgba(0, 0, 0, 0.35)',
        borderColor: 'rgba(255, 255, 255, 0.0)',
        textStyle: { color: '#fff', fontSize: 14 },
        extraCssText: 'backdrop-filter: blur(0px); border-radius: 4px; padding: 6px 10px; max-width: 280px; word-wrap: break-word; white-space: normal;',
        axisPointer: { type: 'line', snap: true, lineStyle: { color: 'rgba(255,255,255,0.18)', width: 1 } },
        formatter: tooltipFormatter
      },
      series,
      // Lane titles
      graphic: lanes.map((lane, idx) => ({
        type: 'text',
        left: 'center',
        top: Math.max(2, (grid[idx].top - laneGap / 2) - laneTitleHeight / 2 + titleNudgeDown),
        style: {
          text: lane.title,
          fill: AXIS_TEXT,
          fontSize: 14,
          fontWeight: 600,
          align: 'center',
          width: 280,
          overflow: 'truncate',
          ellipsis: '...'
        }
      }))
    };

    return { chartOption: opt, chartHeight: containerHeight };
  }, [chartModel, weightUnit, lanes, zoomRange]);

  const option = chartData?.chartOption ?? null;
  const chartHeight = chartData?.chartHeight ?? 800;

  // Pulse the trailing bar of each weekly lane so the in-progress week reads as
  // provisional. Driven off the built option rather than instance.getOption(),
  // which deep-clones the entire chart config on every call.
  useEffect(() => {
    const targets = ['weekly-score', 'weekly-volume']
      .map((id) => {
        const series = option?.series?.find((s) => s?.id === id);
        const data = series?.data;
        const lastIdx = (data?.length ?? 0) - 1;
        if (lastIdx < 0 || !data[lastIdx]?.isPartialWeek) return null;
        return { id, data, lastIdx, lastItem: data[lastIdx] };
      })
      .filter(Boolean);

    if (targets.length === 0) return undefined;

    const startTime = Date.now();
    let lastStep = 0;
    let frame = requestAnimationFrame(function tick() {
      frame = requestAnimationFrame(tick);
      const now = Date.now();
      if (now - lastStep < PULSE_FRAME_MS) return;
      lastStep = now;

      const instance = chartRef.current?.getEchartsInstance?.();
      if (!instance || instance.isDisposed?.()) return;

      const phase = ((now - startTime) / 1000) * PULSE_RAD_PER_SECOND;
      const opacity = 0.5 + 0.5 * (0.5 + 0.5 * Math.sin(phase));

      instance.setOption({
        series: targets.map(({ id, data, lastIdx, lastItem }) => {
          const next = data.slice();
          next[lastIdx] = { ...lastItem, itemStyle: { color: `rgba(255, 255, 255, ${opacity})` } };
          return { id, data: next };
        })
      }, { lazyUpdate: false, silent: true });
    });

    return () => cancelAnimationFrame(frame);
  }, [option]);

  // Create zoom slider option for header
  const zoomSliderOption = useMemo(() => {
    if (!chartModel) return null;

    const { minDate, maxDate, zoomStartValue, zoomEndValue, weeklyVolumePoints } = chartModel;

    // Use weekly volume as the background data for the slider
    const sliderData = (weeklyVolumePoints || []).map(p => [p.t, p.v]);

    return {
      animation: false,
      grid: {
        left: 24,
        right: 24,
        top: 0,
        bottom: 0,
        height: 0
      },
      xAxis: {
        type: 'time',
        show: false,
        min: isFiniteNumber(minDate) ? minDate : 'dataMin',
        max: isFiniteNumber(maxDate) ? maxDate : 'dataMax'
      },
      yAxis: {
        type: 'value',
        show: false
      },
      series: [{
        type: 'line',
        data: sliderData,
        showSymbol: false,
        lineStyle: { opacity: 0 },
        areaStyle: { opacity: 0 }
      }],
      dataZoom: [
        {
          type: 'slider',
          xAxisIndex: 0,
          filterMode: 'none',
          height: 32,
          top: 4,
          left: 24,
          right: 24,
          handleIcon: 'none',
          handleSize: 0,
          moveHandleSize: 32,
          zoomLock: true,
          brushSelect: false,
          showDetail: false,
          realtime: true,
          throttle: 0,
          borderColor: 'rgba(255,255,255,0.12)',
          fillerColor: 'rgba(148,163,184,0.25)',
          backgroundColor: 'rgba(255,255,255,0.06)',
          emphasis: {
            handleStyle: { opacity: 0 },
            moveHandleStyle: { color: 'rgba(148,163,184,0.35)' }
          },
          moveHandleStyle: {
            color: 'transparent'
          },
          dataBackground: {
            lineStyle: { color: 'rgba(255,255,255,0.25)', width: 1 },
            areaStyle: { color: 'rgba(255,255,255,0.08)' }
          },
          selectedDataBackground: {
            lineStyle: { color: 'rgba(255,255,255,0.35)', width: 1 },
            areaStyle: { color: 'rgba(255,255,255,0.12)' }
          },
          startValue: isFiniteNumber(zoomStartValue) ? zoomStartValue : undefined,
          endValue: isFiniteNumber(zoomEndValue) ? zoomEndValue : undefined
        },
        {
          type: 'inside',
          xAxisIndex: 0,
          filterMode: 'none',
          zoomLock: true,
          zoomOnMouseWheel: false,
          moveOnMouseMove: true,
          moveOnMouseWheel: false,
          preventDefaultMouseMove: true,
          throttle: 0
        }
      ]
    };
  }, [chartModel]);

  useEffect(() => {
    if (animateChartIn) return;
    if (!loading && option) setAnimateChartIn(true);
  }, [loading, option, animateChartIn]);

  // Store refs for snappy updates without React re-renders
  const chartModelRef = useRef(chartModel);
  const lanesRef = useRef(lanes);
  const zoomRangeLabelRef = useRef(null);
  const isDraggingRef = useRef(false); // Track when we're handling touch/mouse to skip handleDataZoom
  
  useEffect(() => {
    chartModelRef.current = chartModel;
  }, [chartModel]);
  
  useEffect(() => {
    lanesRef.current = lanes;
  }, [lanes]);

  const handleZoom = (range) => {
    setSelectedRange(range);
    
    // Apply zoom while keeping the current end position
    const zoomInstance = zoomChartRef.current?.getEchartsInstance?.();
    const mainInstance = chartRef.current?.getEchartsInstance?.();
    const model = chartModelRef.current;
    const currentLanes = lanesRef.current;
    
    if (!zoomInstance || !mainInstance || !model || !currentLanes) {
      return;
    }
    
    const { minDate, maxDate, zoomEndValue } = model;
    if (!isFiniteNumber(minDate) || !isFiniteNumber(maxDate)) return;
    
    // Use current end position (from chartModel which gets updated via handleDataZoom)
    const currentEnd = zoomEndValue ?? maxDate;
    const totalRange = maxDate - minDate;
    
    let newStart, newEnd;
    if (range === RANGES.ALL) {
      // Show all data
      newStart = minDate;
      newEnd = maxDate;
    } else {
      // Keep end position, adjust start based on new window size
      newEnd = currentEnd;
      newStart = currentEnd - range;
      
      // If start would be before minDate, shift the window forward
      if (newStart < minDate) {
        newStart = minDate;
        newEnd = Math.min(maxDate, minDate + range);
      }
    }
    
    // Calculate percentages
    const startPercent = ((newStart - minDate) / totalRange) * 100;
    const endPercent = ((newEnd - minDate) / totalRange) * 100;
    
    // Update the zoom slider
    zoomInstance.dispatchAction({
      type: 'dataZoom',
      start: startPercent,
      end: endPercent
    });
    
    // Update the date label
    if (zoomRangeLabelRef.current) {
      const startDate = formatDate(newStart);
      const endDate = formatDate(newEnd);
      zoomRangeLabelRef.current.textContent = `${startDate} — ${endDate}`;
    }
    
    // Update the ref for future quick button clicks
    chartModelRef.current = {
      ...chartModelRef.current,
      zoomStartValue: newStart,
      zoomEndValue: newEnd
    };
    
    // Update zoomRange state to trigger option re-computation
    setZoomRange({ start: newStart, end: newEnd });
  };
  // Handle datazoom from slider and sync to main chart
  const handleDataZoom = (params) => {
    // Skip if we're handling touch/mouse drag - jumpToPosition already updates everything
    if (isDraggingRef.current) return;
    
    const mainInstance = chartRef.current?.getEchartsInstance?.();
    const model = chartModelRef.current;
    const currentLanes = lanesRef.current;
    
    if (!mainInstance || !model) return;
    
    const { minDate, maxDate } = model;
    
    // Use params directly - they contain start/end percentages
    const startPercent = params.start ?? 0;
    const endPercent = params.end ?? 100;
    const totalRange = maxDate - minDate;
    const startValue = minDate + (startPercent / 100) * totalRange;
    const endValue = minDate + (endPercent / 100) * totalRange;
    
    // Update label directly via DOM for snappiness
    if (zoomRangeLabelRef.current) {
      const startDate = formatDate(startValue);
      const endDate = formatDate(endValue);
      zoomRangeLabelRef.current.textContent = `${startDate} — ${endDate}`;
    }
    
    // Update the ref so quick zoom buttons know the current position
    if (chartModelRef.current) {
      chartModelRef.current = {
        ...chartModelRef.current,
        zoomStartValue: startValue,
        zoomEndValue: endValue
      };
    }
    
    // Build xAxis update for all lanes
    const xAxisUpdate = currentLanes.map((_, idx) => ({
      gridIndex: idx,
      min: startValue,
      max: endValue
    }));
    
    // Sync to main chart - use notMerge=false and lazyUpdate=false for immediate update
    mainInstance.setOption({ xAxis: xAxisUpdate }, { notMerge: false, lazyUpdate: false });
  };

  // Initialize zoom range from chartModel
  useEffect(() => {
    if (chartModel?.zoomStartValue && chartModel?.zoomEndValue) {
      setZoomRange({ start: chartModel.zoomStartValue, end: chartModel.zoomEndValue });
    }
  }, [chartModel?.zoomStartValue, chartModel?.zoomEndValue]);

  // Handle click on zoom slider to jump to clicked position
  useEffect(() => {
    const zoomInstance = zoomChartRef.current?.getEchartsInstance?.();
    if (!zoomInstance) return;

    // Get the DOM element for direct event handling (works on mobile)
    const chartDom = zoomInstance.getDom();
    if (!chartDom) return;

    let isDragging = false;
    let dragRect = null;

    // Check if click is inside the current selection (let ECharts handle it natively)
    const jumpToPosition = (clientX) => {
      const model = chartModelRef.current;
      const mainInstance = chartRef.current?.getEchartsInstance?.();
      const currentLanes = lanesRef.current;
      if (!model || !mainInstance || !currentLanes) return;

      const { minDate, maxDate, zoomStartValue, zoomEndValue } = model;
      if (!isFiniteNumber(minDate) || !isFiniteNumber(maxDate)) return;

      const rect = dragRect || chartDom.getBoundingClientRect();
      const width = rect.width;
      const left = 24;
      const right = 24;
      const sliderX = left;
      const sliderXEnd = width - right;
      const sliderWidth = sliderXEnd - sliderX;

      // Calculate position relative to the element
      const relativeX = clientX - rect.left;
      
      // Calculate where on the timeline the touch/click was
      const ratio = Math.max(0, Math.min(1, (relativeX - sliderX) / sliderWidth));
      const totalRange = maxDate - minDate;
      const clickedDate = minDate + ratio * totalRange;
      
      // Get current window size
      const currentStart = zoomStartValue ?? minDate;
      const currentEnd = zoomEndValue ?? maxDate;
      const windowSize = currentEnd - currentStart;
      
      // Center the window on the touched position
      let newStart = clickedDate - windowSize / 2;
      let newEnd = clickedDate + windowSize / 2;
      
      // Clamp to bounds
      if (newStart < minDate) {
        newStart = minDate;
        newEnd = minDate + windowSize;
      }
      if (newEnd > maxDate) {
        newEnd = maxDate;
        newStart = maxDate - windowSize;
      }
      
      // Calculate percentages
      const startPercent = ((newStart - minDate) / totalRange) * 100;
      const endPercent = ((newEnd - minDate) / totalRange) * 100;
      
      // Update the zoom slider (silent, no event)
      zoomInstance.dispatchAction({
        type: 'dataZoom',
        start: startPercent,
        end: endPercent
      });
      
      // Update the date label directly
      if (zoomRangeLabelRef.current) {
        const startDate = formatDate(newStart);
        const endDate = formatDate(newEnd);
        zoomRangeLabelRef.current.textContent = `${startDate} — ${endDate}`;
      }
      
      // Update the ref for future operations
      chartModelRef.current = {
        ...chartModelRef.current,
        zoomStartValue: newStart,
        zoomEndValue: newEnd
      };
      
      // Update main chart directly for snappy response
      const xAxisUpdate = currentLanes.map((_, idx) => ({
        gridIndex: idx,
        min: newStart,
        max: newEnd
      }));
      mainInstance.setOption({ xAxis: xAxisUpdate }, { notMerge: false, lazyUpdate: false });
    };

    const handleMouseMove = (e) => {
      jumpToPosition(e.clientX);
    };

    const handleTouchMove = (e) => {
      if (e.touches.length > 0) {
        e.preventDefault(); // Prevent page scrolling while dragging
        jumpToPosition(e.touches[0].clientX);
      }
    };

    // Bound only for the duration of a drag. A non-passive touchmove on window
    // opts the whole page out of compositor-driven scrolling, so it must not
    // linger for the lifetime of the modal.
    const attachDragListeners = () => {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', endDrag);
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', endDrag);
      window.addEventListener('touchcancel', endDrag);
    };

    const detachDragListeners = () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', endDrag);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', endDrag);
      window.removeEventListener('touchcancel', endDrag);
    };

    function startDrag(clientX) {
      isDragging = true;
      isDraggingRef.current = true;
      // The slider cannot move mid-drag, so measure once rather than per event.
      dragRect = chartDom.getBoundingClientRect();
      attachDragListeners();
      jumpToPosition(clientX);
    }

    function endDrag() {
      if (!isDragging) return;
      isDragging = false;
      isDraggingRef.current = false;
      dragRect = null;
      detachDragListeners();
    }

    const handleMouseDown = (e) => {
      startDrag(e.clientX);
      e.preventDefault();
      e.stopPropagation();
    };

    const handleTouchStart = (e) => {
      if (e.touches.length > 0) {
        startDrag(e.touches[0].clientX);
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // Use capture phase to intercept events before ECharts internal handlers
    chartDom.addEventListener('mousedown', handleMouseDown, { capture: true });
    chartDom.addEventListener('touchstart', handleTouchStart, { capture: true, passive: false });

    return () => {
      chartDom.removeEventListener('mousedown', handleMouseDown, { capture: true });
      chartDom.removeEventListener('touchstart', handleTouchStart, { capture: true });
      detachDragListeners();
    };
  }, [option]); // Re-attach when option changes (chart is ready)

  // Format the zoom range for display
  const zoomRangeLabel = useMemo(() => {
    if (!zoomRange.start || !zoomRange.end) return '';
    const startDate = formatDate(zoomRange.start);
    const endDate = formatDate(zoomRange.end);
    return `${startDate} — ${endDate}`;
  }, [zoomRange]);

  if (loading) {
    return (
      <div className="stats-modal-content loading" onClick={e => e.stopPropagation()}>
        <ModalLoading text="Loading Stats..." />
      </div>
    );
  }

  return (
    <div className="stats-modal-content" onClick={e => e.stopPropagation()}>
      <div className="stats-modal-header">
        <h2>Your Progress</h2>
        <div className="stats-time-controls">
          <button className={selectedRange === RANGES.MONTH ? 'active' : ''} onClick={() => handleZoom(RANGES.MONTH)}>1M</button>
          <button className={selectedRange === RANGES.THREE_MONTHS ? 'active' : ''} onClick={() => handleZoom(RANGES.THREE_MONTHS)}>3M</button>
          <button className={selectedRange === RANGES.YEAR ? 'active' : ''} onClick={() => handleZoom(RANGES.YEAR)}>1Y</button>
          <button className={selectedRange === RANGES.FIVE_YEARS ? 'active' : ''} onClick={() => handleZoom(RANGES.FIVE_YEARS)}>5Y</button>
          <button className={selectedRange === RANGES.ALL ? 'active' : ''} onClick={() => handleZoom(RANGES.ALL)}>All</button>
        </div>
        {zoomSliderOption && (
          <>
            <div className="stats-zoom-slider">
              <ReactECharts
                ref={zoomChartRef}
                option={zoomSliderOption}
                notMerge={true}
                lazyUpdate={true}
                style={{ height: 44, width: '100%' }}
                onEvents={{
                  datazoom: handleDataZoom
                }}
              />
            </div>
            <div className="stats-zoom-range-label" ref={zoomRangeLabelRef}>{zoomRangeLabel}</div>
          </>
        )}
      </div>

      <div className="charts-container">
        {!option ? (
          <p className="no-data">No stats recorded yet.</p>
        ) : (
          <div className="chart-section" style={{ minHeight: chartHeight }}>
            <div className={`stats-echarts-wrap${animateChartIn ? ' animate-in' : ''}`} ref={setChartAreaEl} style={{ height: chartHeight }}>
              <ReactECharts
                ref={chartRef}
                option={option}
                notMerge={true}
                lazyUpdate={true}
                style={{ height: chartHeight, width: '100%' }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StatsModal;
